<template>
  <main
    class="page"
    role="main"
    aria-labelledby="page-title"
  >
    <section class="card lp">
      <header class="page-header">
        <img
          class="page-header-logo"
          :src="logoNaschba"
          alt="Logo NaschBa"
          height="144"
        >
        <div class="page-header-content w-100 text-center">
          <h1
            id="page-title"
            class="h1 mt-0"
          >
            Schulanmeldung
          </h1>
          <p
            v-if="!successId"
            class="muted mt-5"
          >
            Bitte fülle die Angaben Schritt für Schritt aus. Pflichtfelder sind markiert.
          </p>
        </div>
      </header>

      <p
        v-if="error"
        class="notice notice-danger mt-12"
        role="alert"
      >
        {{ error }}
      </p>

      <section
        v-else-if="successId"
        class="success-view mt-12"
        aria-label="Anmeldung erfolgreich"
      >
        <p
          class="notice notice-success text-center"
          role="status"
        >
          Vielen Dank! Deine Schulanmeldung ist bei uns eingegangen.
        </p>

        <p class="mt-12">
          Wir melden uns bei dir, sobald wir die Unterlagen geprüft haben.
        </p>

        <p
          v-if="successEmailSent === true"
          class="notice mt-12"
          role="status"
        >
          Du hast eine Eingangsbestätigung per E‑Mail erhalten.
        </p>
        <p
          v-else
          class="muted mt-12"
        >
          Hinweis: Du hast keine Bestätigungs‑E‑Mail erhalten (z. B. weil keine E‑Mail-Adresse angegeben wurde).
        </p>

        <div class="actions mt-12">
          <button
            type="button"
            class="btn"
            @click="resetForm"
          >
            Neue Anmeldung ausfüllen
          </button>
        </div>
      </section>

      <form
        v-else
        class="form mt-12"
        @submit.prevent="submit"
      >
        <!-- Abschnitt: Antrag -->
        <section class="section">
          <h2 class="h2">
            Anmeldeantrag an der Freie Naturschule Barnim (NaschBa), 16359 Biesenthal
          </h2>

          <div
            class="grid2"
            style="grid-template-columns: 40% 20% 20%"
          >
            <div class="field mb-3">
              <label class="m-0">Anmeldung für die <span class="req">*</span></label>

              <div
                id="schoolType"
                class="row-inline"
                role="radiogroup"
                aria-label="Anmeldeantrag für die Schulform"
                :aria-invalid="Boolean(fieldErrors.schoolType)"
                :aria-describedby="fieldErrors.schoolType ? errorId('schoolType') : null"
              >
                <label class="check">
                  <input
                    v-model="form.schoolType"
                    type="radio"
                    name="schoolType"
                    value="grundschule"
                  >
                  <span>Grundschule</span>
                </label>

                <label class="check">
                  <input
                    v-model="form.schoolType"
                    type="radio"
                    name="schoolType"
                    value="sek1"
                  >
                  <span style="white-space: nowrap;">Sekundarstufe I</span>
                </label>
              </div>

              <p
                v-if="fieldErrors.schoolType"
                :id="errorId('schoolType')"
                class="help danger"
                role="alert"
              >
                {{ fieldErrors.schoolType }}
              </p>
            </div>

            <div class="field mb-3">
              <label for="schoolYear">Schuljahr <span class="req">*</span></label>
              <input
                id="schoolYear"
                v-model="form.schoolYear"
                class="input"
                type="text"
                placeholder="z. B. 2026/2027"
                :aria-invalid="Boolean(fieldErrors.schoolYear)"
                :aria-describedby="fieldErrors.schoolYear ? errorId('schoolYear') : null"
              >
              <p
                v-if="fieldErrors.schoolYear"
                :id="errorId('schoolYear')"
                class="help danger"
                role="alert"
              >
                {{ fieldErrors.schoolYear }}
              </p>
            </div>

            <div class="field mb-3">
              <label for="classLevel">Klasse <span class="req">*</span></label>
              <input
                id="classLevel"
                v-model="form.classLevel"
                class="input"
                type="text"
                placeholder="z. B. 1"
                :aria-invalid="Boolean(fieldErrors.classLevel)"
                :aria-describedby="fieldErrors.classLevel ? errorId('classLevel') : null"
              >
              <p
                v-if="fieldErrors.classLevel"
                :id="errorId('classLevel')"
                class="help danger"
                role="alert"
              >
                {{ fieldErrors.classLevel }}
              </p>
            </div>
          </div>
        </section>

        <!-- Abschnitt: Kind -->
        <section class="section">
          <h2 class="h2">
            Angaben zum Kind
          </h2>

          <div class="grid2">
            <div class="field">
              <label for="childFirstName">Vorname <span class="req">*</span></label>
              <input
                id="childFirstName"
                v-model="form.childFirstName"
                class="input"
                type="text"
                autocomplete="given-name"
                :aria-invalid="Boolean(fieldErrors.childFirstName)"
                :aria-describedby="fieldErrors.childFirstName ? errorId('childFirstName') : null"
              >
              <p
                v-if="fieldErrors.childFirstName"
                :id="errorId('childFirstName')"
                class="help danger"
                role="alert"
              >
                {{ fieldErrors.childFirstName }}
              </p>
            </div>

            <div class="field">
              <label for="childLastName">Nachname <span class="req">*</span></label>
              <input
                id="childLastName"
                v-model="form.childLastName"
                class="input"
                type="text"
                autocomplete="family-name"
                :aria-invalid="Boolean(fieldErrors.childLastName)"
                :aria-describedby="fieldErrors.childLastName ? errorId('childLastName') : null"
              >
              <p
                v-if="fieldErrors.childLastName"
                :id="errorId('childLastName')"
                class="help danger"
                role="alert"
              >
                {{ fieldErrors.childLastName }}
              </p>
            </div>

            <div class="field">
              <label for="childBirthDate">Geburtsdatum <span class="req">*</span></label>
              <FlatPickr
                id="childBirthDate"
                v-model="form.childBirthDate"
                class="input"
                :config="birthDateConfig"
                placeholder="TT.MM.JJJJ"
                :aria-invalid="Boolean(fieldErrors.childBirthDate)"
                :aria-describedby="fieldErrors.childBirthDate ? errorId('childBirthDate') : null"
              />
              <p
                v-if="fieldErrors.childBirthDate"
                :id="errorId('childBirthDate')"
                class="help danger"
                role="alert"
              >
                {{ fieldErrors.childBirthDate }}
              </p>
            </div>

            <div class="field">
              <label for="childGender">Geschlecht <span class="req">*</span></label>
              <select
                id="childGender"
                v-model="form.childGender"
                class="input"
                :aria-invalid="Boolean(fieldErrors.childGender)"
                :aria-describedby="fieldErrors.childGender ? errorId('childGender') : null"
              >
                <option value="">
                  -- bitte auswählen --
                </option>
                <option value="weiblich">
                  weiblich
                </option>
                <option value="maennlich">
                  männlich
                </option>
                <option value="unbestimmt">
                  unbestimmt
                </option>
              </select>
              <p
                v-if="fieldErrors.childGender"
                :id="errorId('childGender')"
                class="help danger"
                role="alert"
              >
                {{ fieldErrors.childGender }}
              </p>
            </div>
          </div>

          <div
            class="grid2"
            style="grid-template-columns: .5fr .5fr 2fr"
          >
            <div class="field">
              <label for="nationality">Staatsangehörigkeit</label>
              <input
                id="nationality"
                v-model="form.nationality"
                class="input"
                type="text"
              >
            </div>

            <div class="field">
              <label>Besonderer Förderbedarf?</label>
              <div class="row-inline">
                <label class="check">
                  <input
                    v-model="form.specialSupportNeeded"
                    type="radio"
                    :value="false"
                  >
                  <span>Nein</span>
                </label>
                <label class="check">
                  <input
                    v-model="form.specialSupportNeeded"
                    type="radio"
                    :value="true"
                  >
                  <span>Ja</span>
                </label>
              </div>
            </div>

            <div
              v-if="form.specialSupportNeeded === true"
              class="field"
              style="align-items: end;"
            >
              <label />
              <input
                v-model="form.specialSupportNote"
                class="input"
                type="text"
                placeholder="Wenn ja, welcher? (optional)"
              >
            </div>
          </div>
        </section>

        <!-- Abschnitt: Anschrift -->
        <section class="section">
          <h2 class="h2">
            Anschrift
          </h2>

          <div
            class="grid2"
            style="grid-template-columns: 1.8fr .4fr 1.1fr"
          >
            <div class="field">
              <label for="addressStreet">Straße und Haus-Nr.</label>
              <input
                id="addressStreet"
                v-model="form.addressStreet"
                class="input"
                type="text"
                autocomplete="street-address"
              >
            </div>

            <div class="field">
              <label for="addressZip">PLZ</label>
              <input
                id="addressZip"
                v-model="form.addressZip"
                class="input"
                type="text"
                autocomplete="postal-code"
              >
            </div>

            <div class="field">
              <label for="addressCity">Wohnort</label>
              <input
                id="addressCity"
                v-model="form.addressCity"
                class="input"
                type="text"
                autocomplete="address-level2"
              >
            </div>
          </div>
        </section>

        <!-- Abschnitt: Eltern -->
        <section class="section">
          <h2 class="h2">
            Angaben zu den Eltern
          </h2>

          <div
            class="grid2 md:mb-0 mb-4 "
            style="grid-template-columns: .6fr .7fr .5fr .7fr"
          >
            <div class="field mb-1">
              <label for="motherName">Name der Mutter</label>
              <input
                id="motherName"
                v-model="form.motherName"
                class="input"
                type="text"
              >
            </div>
            <div class="field mb-1">
              <label for="motherJob">Beruf</label>
              <input
                id="motherJob"
                v-model="form.motherJob"
                class="input"
                type="text"
              >
            </div>
            <div class="field mb-1">
              <label for="motherPhone">Telefon</label>
              <input
                id="motherPhone"
                v-model="form.motherPhone"
                class="input"
                type="tel"
                autocomplete="tel"
              >
            </div>
            <div class="field mb-1">
              <label for="motherEmail">E‑Mail</label>
              <input
                id="motherEmail"
                v-model="form.motherEmail"
                class="input"
                type="email"
                autocomplete="email"
              >
            </div>
          </div>
          <div
            class="grid2"
            style="grid-template-columns: .6fr .7fr .5fr .7fr"
          >
            <div class="field">
              <label for="fatherName">Name des Vaters</label>
              <input
                id="fatherName"
                v-model="form.fatherName"
                class="input"
                type="text"
              >
            </div>
            <div class="field">
              <label for="fatherJob">Beruf</label>
              <input
                id="fatherJob"
                v-model="form.fatherJob"
                class="input"
                type="text"
              >
            </div>
            <div class="field">
              <label for="fatherPhone">Telefon</label>
              <input
                id="fatherPhone"
                v-model="form.fatherPhone"
                class="input"
                type="tel"
              >
            </div>
            <div class="field">
              <label for="fatherEmail">E‑Mail</label>
              <input
                id="fatherEmail"
                v-model="form.fatherEmail"
                class="input"
                type="email"
              >
            </div>
          </div>
        </section>

        <!-- Abschnitt: Familiensituation -->
        <section class="section">
          <h2 class="h2">
            Familiensituation
          </h2>

          <div
            class="grid2"
            style="grid-template-columns: .5fr .5fr 1fr"
          >
            <div class="field mb-2">
              <label for="livingSituation">Wir leben …</label>
              <select
                id="livingSituation"
                v-model="form.livingSituation"
                class="input"
              >
                <option value="">
                  Bitte wähle
                </option>
                <option value="zusammen">
                  zusammen mit dem Kind
                </option>
                <option value="getrennt">
                  getrennt
                </option>
              </select>
            </div>

            <div class="field mb-2">
              <label for="childLivesMainlyWith">Kind lebt hauptsächlich bei</label>
              <input
                id="childLivesMainlyWith"
                v-model="form.childLivesMainlyWith"
                class="input"
                type="text"
                placeholder="z. B. Mutter/Vater/…"
              >
            </div>

            <div
              class="field mb-2"
              style="align-items: end;"
            >
              <label class="check">
                <input
                  v-model="form.childLivesParity"
                  style="width: 20px;"
                  type="checkbox"
                >
                <span>Kind lebt paritätisch bei beiden Elternteilen</span>
              </label>
            </div>
          </div>

          <div
            class="grid2"
            style="grid-template-columns: .5fr 1fr .5fr; align-items: end;"
          >
            <div class="field">
              <label for="custody">Sorgerecht</label>
              <select
                id="custody"
                v-model="form.custody"
                class="input"
              >
                <option value="">
                  Bitte wähle
                </option>
                <option value="gemeinsam">
                  gemeinsam
                </option>
                <option value="bei">
                  liegt bei (siehe Feld)
                </option>
                <option value="unbekannt">
                  unbekannt
                </option>
              </select>
            </div>
            <div
              v-if="form.custody === 'bei'"
              class="field"
            >
              <input
                v-model="form.custodyNote"
                class="input"
                type="text"
                placeholder="Sorgerecht liegt bei …"
              >
            </div>
          </div>
        </section>

        <!-- Abschnitt: Einrichtung / Geschwister -->
        <section class="section">
          <h2 class="h2">
            Weitere Angaben
          </h2>

          <div class="field mb-4">
            <label for="currentInstitution">Aktuell besuchte Einrichtung</label>
            <input
              id="currentInstitution"
              v-model="form.currentInstitution"
              class="input"
              type="text"
            >
          </div>

          <div class="field mb-2">
            <label>Weitere Geschwisterkinder mit perspektivischem Aufnahmewunsch</label>

            <div
              v-for="(s, idx) in form.siblings"
              :key="idx"
              class="sibling-row"
            >
              <input
                v-model="s.name"
                class="input"
                type="text"
                placeholder="Name"
              >
              <FlatPickr
                v-model="s.birthDate"
                class="input"
                :config="birthDateConfig"
                placeholder="Geburtstag"
              />
              <input
                v-model="s.schoolYear"
                class="input"
                type="text"
                placeholder="Schuljahr (z. B. 2027/2028)"
              >
              <input
                v-model="s.classLevel"
                class="input"
                type="text"
                placeholder="Klasse"
              >
              <button
                v-if="form.siblings.length > 1"
                type="button"
                class="btn hover:bg-gray-200 bg-gray-100"
                @click="removeSiblingRow(idx)"
              >
                Entfernen
              </button>
            </div>

            <div class="row mt-3">
              <button
                type="button"
                class="col-3 btn hover:bg-gray-200 bg-gray-100 d-block"
                @click="addSiblingRow"
              >
                Zeile hinzufügen
              </button>
            </div>
          </div>

          <div class="field mt-3">
            <label for="notes">Besonderheiten / Wünsche</label>
            <textarea
              id="notes"
              v-model="form.notes"
              class="input"
              rows="5"
            />
          </div>
        </section>

        <!-- Abschnitt: Optional -->
        <section class="section">
          <h2 class="h2">
            Optional (Ein paar Fragen für Eltern und/oder Kind)
          </h2>

          <div class="field mb-2">
            <label for="favoriteSeason">Lieblingsjahreszeit</label>
            <input
              id="favoriteSeason"
              v-model="form.favoriteSeason"
              class="input"
              type="text"
            >
          </div>

          <div class="field mb-2">
            <label for="likes">Das mag ich</label>
            <textarea
              id="likes"
              v-model="form.likes"
              class="input"
              rows="3"
            />
          </div>

          <div class="field mb-2">
            <label for="dislikes">Das mag ich nicht so gerne</label>
            <textarea
              id="dislikes"
              v-model="form.dislikes"
              class="input"
              rows="3"
            />
          </div>

          <div class="field mb-2">
            <label for="proudOf">Darauf bin ich stolz</label>
            <textarea
              id="proudOf"
              v-model="form.proudOf"
              class="input"
              rows="3"
            />
          </div>

          <div class="field mb-2">
            <label for="dreamSchoolDescription">Wenn ich eine Schule bauen könnte, würde sie so aussehen …</label>
            <textarea
              id="dreamSchoolDescription"
              v-model="form.dreamSchoolDescription"
              class="input"
              rows="4"
            />
          </div>
        </section>

        <!-- Abschnitt: Datenschutz -->
        <section class="section mt-8">
          <h2 class="h2">
            Einwilligung <span class="req">*</span>
          </h2>

          <label
            class="check"
            style="align-items: baseline;"
          >
            <input
              id="privacyAccepted"
              v-model="form.privacyAccepted"
              type="checkbox"
              :aria-invalid="Boolean(fieldErrors.privacyAccepted)"
              :aria-describedby="fieldErrors.privacyAccepted ? errorId('privacyAccepted') : null"
            >
            <span style="line-height: 20px;">
              Mit der Einsendung dieses Anmeldebogens stimmst Du der Speicherung der angegebenen Daten zum Zweck der
              Aufnahmeprüfung und Führung der Warteliste gemäß Art. 6 Abs. 1 lit. b DSGVO zu. Details entnimmst Du
              unserer <button
                type="button"
                class="link"
                @click="goTo('Datenschutz', { blank: true })"
              >Datenschutzerklärung</button>. Du kannst Deine Bewerbung jederzeit schriftlich zurückziehen.
            </span>
          </label>
          <p
            v-if="fieldErrors.privacyAccepted"
            :id="errorId('privacyAccepted')"
            class="help danger"
            role="alert"
          >
            {{ fieldErrors.privacyAccepted }}
          </p>
        </section>

        <div class="actions">
          <button
            class="btn btn-primary"
            type="submit"
            :disabled="submitting"
          >
            <span
              v-if="submitting"
              class="spinner"
              aria-hidden="true"
            />
            Anmeldung senden
          </button>
        </div>
      </form>
    </section>
    <footer
      class="legal-footer"
      role="contentinfo"
      aria-label="Rechtliche Informationen"
    >
      <nav aria-label="Rechtliche Links">
        <router-link
          to="/impressum"
          class="legal-link"
          target="_blank"
          rel="noopener"
        >
          Impressum
        </router-link>
        <span
          class="sep"
          aria-hidden="true"
        >·</span>
        <router-link
          to="/datenschutz"
          class="legal-link"
          target="_blank"
          rel="noopener"
        >
          Datenschutz
        </router-link>
      </nav>
    </footer>
  </main>
</template>

<script setup>
import {computed, nextTick, ref} from 'vue'
import {useRouter} from 'vue-router'
import api from '@/services/api'

// optional: FlatPickr (im Projekt vorhanden)
import FlatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css'
import {German} from 'flatpickr/dist/l10n/de.js'

const logoNaschba = new URL('@/assets/logo-naschba.jpg', import.meta.url).href
const router = useRouter()

const submitting = ref(false)
const successId = ref(null)
const successEmailSent = ref(null)
const error = ref('')

const form = ref({
  // Kind
  childFirstName: '',
  childLastName: '',
  childBirthDate: null,
  childGender: '', // weiblich|maennlich|unbestimmt
  nationality: '',
  specialSupportNeeded: null, // true|false|null
  specialSupportNote: '',

  // Wunsch / Start
  schoolYear: '',
  classLevel: '',

  // Anschrift Kind (optional, aber häufig hilfreich)
  addressStreet: '',
  addressZip: '',
  addressCity: '',

  // Eltern
  motherName: '',
  motherJob: '',
  motherPhone: '',
  motherEmail: '',
  fatherName: '',
  fatherJob: '',
  fatherPhone: '',
  fatherEmail: '',

  // Familiensituation
  livingSituation: '', // zusammen|getrennt
  childLivesMainlyWith: '',
  childLivesParity: false,
  custody: '', // gemeinsam|bei|unbekannt
  custodyNote: '',

  // Aktuelle Einrichtung / Geschwister
  currentInstitution: '',
  siblings: [
    {name: '', birthDate: null, schoolYear: '', classLevel: ''},
  ],

  // Besonderheiten / Wünsche
  notes: '',

  // Optional-Fragen
  favoriteSeason: '',
  likes: '',
  dislikes: '',
  proudOf: '',
  dreamSchoolDescription: '',

  // Einwilligung
  privacyAccepted: false,
})

function normalizeDeDate(val) {
  const s = String(val ?? '').trim()
  if (!s) return null

  // akzeptiere bereits DD.MM.YYYY
  const m1 = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(s)
  if (m1) return s

  // akzeptiere YYYY-MM-DD und wandle um
  const m2 = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s)
  if (m2) return `${m2[3]}.${m2[2]}.${m2[1]}`

  return s
}

const birthDateConfig = computed(() => ({
  dateFormat: 'd.m.Y',     // gespeicherter Wert (v-model) => DD.MM.YYYY
  allowInput: true,
  locale: German,
  disableMobile: true,
}))

function addSiblingRow() {
  form.value.siblings.push({name: '', birthDate: null, schoolYear: '', classLevel: ''})
}

function removeSiblingRow(idx) {
  form.value.siblings.splice(idx, 1)
}

const fieldErrors = ref({})

function errorId(field) {
  return `${field}-error`
}

function clearFieldErrors() {
  fieldErrors.value = {}
}

async function scrollToFirstFieldError() {
  await nextTick()

  const order = [
    'schoolType',
    'schoolYear',
    'classLevel',
    'childFirstName',
    'childLastName',
    'childBirthDate',
    'childGender',
    'privacyAccepted',
  ]

  const first = order.find((k) => fieldErrors.value && fieldErrors.value[k])
  if (!first) return

  const el = document.getElementById(first)
  if (!el) return

  el.scrollIntoView({behavior: 'smooth', block: 'center'})
  if (typeof el.focus === 'function') {
    el.focus({preventScroll: true})
  }
}

function validateSchoolYearFormat(val) {
  const s = String(val ?? '').trim()
  if (!s) return {ok: false, message: 'Bitte Schuljahr angeben.'}

  // Format: JJJJ/JJJJ
  const m = /^(\d{4})\/(\d{4})$/.exec(s)
  if (!m) return {ok: false, message: 'Bitte im Format JJJJ/JJJJ angeben (z. B. 2026/2027).'}

  const y1 = Number(m[1])
  const y2 = Number(m[2])

  // optional streng: Folgejahr muss +1 sein
  if (!Number.isFinite(y1) || !Number.isFinite(y2) || y2 !== y1 + 1) {
    return {ok: false, message: 'Bitte ein gültiges Schuljahr angeben (z. B. 2026/2027).'}
  }

  return {ok: true, message: ''}
}

function validateClassLevelInteger(val) {
  const s = String(val ?? '').trim()
  if (!s) return {ok: false, message: 'Bitte die Klasse angeben.'}

  // nur positive Ganzzahl (1,2,3,...)
  if (!/^\d+$/.test(s)) {
    return {ok: false, message: 'Klasse muss eine ganze Zahl sein (z. B. 1).'}
  }

  const n = Number(s)
  if (!Number.isFinite(n) || n <= 0) {
    return {ok: false, message: 'Bitte eine gültige Klasse angeben (z. B. 1).'}
  }

  return {ok: true, message: ''}
}

function validate() {
  error.value = ''
  clearFieldErrors()

  if (!String(form.value.schoolType || '').trim()) {
    fieldErrors.value.schoolType = 'Bitte eine Schulform auswählen.'
  }

  const syCheck = validateSchoolYearFormat(form.value.schoolYear)
  if (!syCheck.ok) {
    fieldErrors.value.schoolYear = syCheck.message
  }

  const clCheck = validateClassLevelInteger(form.value.classLevel)
  if (!clCheck.ok) {
    fieldErrors.value.classLevel = clCheck.message
  }

  if (!form.value.schoolYear.trim()) {
    fieldErrors.value.schoolYear = 'Bitte Schuljahr angeben.'
  }

  if (!String(form.value.classLevel || '').trim()) {
    fieldErrors.value.classLevel = 'Bitte die Klasse angeben.'
  }

  if (!form.value.childFirstName.trim()) {
    fieldErrors.value.childFirstName = 'Bitte den Vornamen des Kindes angeben.'
  }

  if (!form.value.childLastName.trim()) {
    fieldErrors.value.childLastName = 'Bitte den Nachnamen des Kindes angeben.'
  }

  const bd = normalizeDeDate(form.value.childBirthDate)
  if (!bd || !/^\d{2}\.\d{2}\.\d{4}$/.test(bd)) {
    fieldErrors.value.childBirthDate = 'Bitte Datum angeben.'
  }

  if (!String(form.value.childGender || '').trim()) {
    fieldErrors.value.childGender = 'Bitte auswählen.'
  }

  if (!form.value.privacyAccepted) {
    fieldErrors.value.privacyAccepted = 'Bitte bestätige den Datenschutz, um die Anmeldung zu senden.'
  }

  if (Object.keys(fieldErrors.value).length > 0) {
    scrollToFirstFieldError()
    return false
  }

  return true
}

function resetForm() {
  successId.value = null
  successEmailSent.value = null
  error.value = ''
  clearFieldErrors()

  form.value = {
    childFirstName: '',
    childLastName: '',
    childBirthDate: null,
    childGender: '',
    nationality: '',
    specialSupportNeeded: null,
    specialSupportNote: '',

    schoolYear: '',
    classLevel: '',
    schoolType: '',

    addressStreet: '',
    addressZip: '',
    addressCity: '',

    motherName: '',
    motherJob: '',
    motherPhone: '',
    motherEmail: '',
    fatherName: '',
    fatherJob: '',
    fatherPhone: '',
    fatherEmail: '',

    livingSituation: '',
    childLivesMainlyWith: '',
    childLivesParity: false,
    custody: '',
    custodyNote: '',

    currentInstitution: '',
    siblings: [{name: '', birthDate: null, schoolYear: '', classLevel: ''}],

    notes: '',

    favoriteSeason: '',
    likes: '',
    dislikes: '',
    proudOf: '',
    dreamSchoolDescription: '',

    privacyAccepted: false,
  }
}

async function submit() {
  if (!validate()) return

  submitting.value = true
  successId.value = null
  error.value = ''

  try {
    const payload = {
      childFirstName: form.value.childFirstName.trim(),
      childLastName: form.value.childLastName.trim(),
      childBirthDate: normalizeDeDate(form.value.childBirthDate),
      schoolYear: form.value.schoolYear.trim(),
      classLevel: form.value.classLevel?.trim() || null,
      data: {
        schoolType: String(form.value.schoolType || '').trim() || null,
        childGender: form.value.childGender,
        nationality: form.value.nationality?.trim() || null,
        specialSupportNeeded: form.value.specialSupportNeeded,
        specialSupportNote: form.value.specialSupportNote?.trim() || null,

        addressStreet: form.value.addressStreet?.trim() || null,
        addressZip: form.value.addressZip?.trim() || null,
        addressCity: form.value.addressCity?.trim() || null,

        mother: {
          name: form.value.motherName?.trim() || null,
          job: form.value.motherJob?.trim() || null,
          phone: form.value.motherPhone?.trim() || null,
          email: form.value.motherEmail?.trim() || null,
        },
        father: {
          name: form.value.fatherName?.trim() || null,
          job: form.value.fatherJob?.trim() || null,
          phone: form.value.fatherPhone?.trim() || null,
          email: form.value.fatherEmail?.trim() || null,
        },

        family: {
          livingSituation: form.value.livingSituation || null,
          childLivesMainlyWith: form.value.childLivesMainlyWith?.trim() || null,
          childLivesParity: Boolean(form.value.childLivesParity),
          custody: form.value.custody || null,
          custodyNote: form.value.custodyNote?.trim() || null,
        },

        currentInstitution: form.value.currentInstitution?.trim() || null,
        siblings: (form.value.siblings || [])
            .filter((s) =>
                (s.name || '').trim() ||
                (s.schoolYear || '').trim() ||
                (s.classLevel || '').trim() ||
                Boolean(normalizeDeDate(s.birthDate))
            )
            .map((s) => ({
              name: (s.name || '').trim() || null,
              birthDate: normalizeDeDate(s.birthDate),
              schoolYear: (s.schoolYear || '').trim() || null,
              classLevel: (s.classLevel || '').trim() || null
            })),

        notes: form.value.notes?.trim() || null,

        optional: {
          favoriteSeason: form.value.favoriteSeason?.trim() || null,
          likes: form.value.likes?.trim() || null,
          dislikes: form.value.dislikes?.trim() || null,
          proudOf: form.value.proudOf?.trim() || null,
          dreamSchoolDescription: form.value.dreamSchoolDescription?.trim() || null,
        },
      },
    }

    const res = await api.post('/public/school-enrollments', payload)
    successId.value = res?.data?.id ?? true
    successEmailSent.value = res?.data?.emailSent === true
  } catch (e) {
    error.value =
        e?.response?.data?.message ||
        e?.message ||
        'Senden fehlgeschlagen. Bitte versuch es später erneut.'
  } finally {
    submitting.value = false
  }
}

const goTo = (routeName, {blank = false} = {}) => {
  const resolved = router.resolve({name: routeName})

  if (blank) {
    window.open(resolved.href, '_blank', 'noopener')
    return
  }

  router.push({name: routeName})
}
</script>

<style scoped>
.page {
  padding: 16px;
  background: var(--bg);
  color: var(--text);
}

.page-header {
  border-bottom: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 0;
}

.success-view {
  display: grid;
  gap: 12px;
}

.page-header-content {
  flex: 1;
  min-width: 0;
}

.page-header-logo {
  height: 144px;
  width: auto;
  display: block;
  object-fit: contain;
  margin: 20px 0 0 20px;
}

.card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 16px;
}

.lp {
  max-width: 980px;
  margin: 0 auto;
}

.h1 {
  font-size: 26px;
  line-height: 24px;
  font-weight: 600;
  margin: 0;
}

.h2 {
  font-size: 16px;
  line-height: 22px;
  font-weight: 600;
  margin: 0;
}

.muted {
  color: var(--muted);
}

.mt-0 {
  margin-top: 0;
}

.mt-8 {
  margin-top: 8px;
}

.mt-12 {
  margin-top: 12px;
}

.form {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 5px;
}

.field label {
  margin-bottom: 0;
}

.field {
  gap: 2px !important;
}

.section {
  border-top: 1px solid var(--border);
  padding-top: 16px;
  margin-top: 0;
  display: grid;
  gap: 12px;
}

.field {
  display: grid;
  gap: 6px;
}

.req {
  color: var(--danger);
}

.input {
  min-height: 40px;
  padding: 8px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--surface);
  color: var(--text);
}

.input:focus {
  outline: none;
  box-shadow: 0 0 0 3px var(--focus);
  border-color: var(--primary);
}

.help {
  font-size: 12px;
  line-height: 16px;
  color: var(--muted);
  margin: 0;
}

.help.danger {
  color: var(--danger);
}

.grid2 {
  display: grid;
  grid-template-columns: 1fr 1fr .5fr .5fr;
  gap: 12px;
}

.grid3 {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 12px;
}

.grid2full {
  grid-column: 1 / -1;
}

.row-inline {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.check {
  display: inline-flex;
  gap: 8px;
  align-items: center;
  min-height: 44px;
}

.actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
}

.notice {
  border: 1px solid var(--border);
  border-radius: 6px;
  padding: 10px 12px;
  background: var(--surface);
}

.notice-danger {
  border-color: rgba(239, 68, 68, 0.35);
  background: rgba(239, 68, 68, 0.08);
}

.notice-success {
  border-color: rgba(16, 185, 129, 0.35);
  background: rgba(16, 185, 129, 0.08);
}

.btn {
  height: 40px;
  padding: 0 14px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--text);
}

.btn-primary {
  background: var(--primary);
  border-color: var(--primary);
  color: #fff;
}

.btn-primary:disabled {
  opacity: 0.7;
}

.link {
  border: 0;
  background: transparent;
  padding: 0;
  color: var(--primary);
  text-decoration: underline;
  cursor: pointer;
}

.legal-footer {
  position: relative;
  bottom: 0;
  margin-top: 10px;
}

.spinner {
  width: 16px;
  height: 16px;
  border-radius: 999px;
  border: 2px solid rgba(255, 255, 255, 0.55);
  border-top-color: #fff;
  display: inline-block;
  margin-right: 8px;
  animation: spin 0.9s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.sibling-row {
  display: grid;
  grid-template-columns: 1.5fr 0.5fr 0.8fr 0.5fr auto;
  gap: 8px;
  align-items: center;
  margin-top: 8px;
}

.legal-footer {
  position: relative;
  display: flex;
  justify-content: center;
  padding: 0 12px;
  pointer-events: none;
  margin-top: 12px;
}

.legal-footer nav {
  background: var(--surface, #FFFFFF);
  border: 1px solid var(--border, #E0E3EA);
  border-radius: 999px;
  padding: 6px 10px;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.08);
  pointer-events: auto;
}

.legal-link {
  color: var(--text, #1F2937);
  text-decoration: none;
  font-size: 13px;
  line-height: 18px;
  padding: 0 6px;
}

.legal-link:hover,
.legal-link:focus-visible {
  text-decoration: underline;
  outline: none;
}

.sep {
  color: var(--muted, #6B7280);
  margin: 0 2px;
}


@media (max-width: 768px) {
  .grid2 {
    grid-template-columns: 1fr;
  }

  .actions {
    justify-content: stretch;
  }

  .btn, .btn-primary {
    width: 100%;
  }
}

/* NEU: Zwischen-Layout (481px–768px) */
@media (max-width: 768px) and (min-width: 481px) {
  .page-header {
    align-items: flex-start;
  }

  .page-header-logo {
    height: 112px;
    margin: 12px 0 0 12px;
  }

  .card {
    padding: 14px;
  }

  /* kurze Felder nebeneinander, lange Felder untereinander */
  .grid2 {
    grid-template-columns: 1fr 1fr !important;
    gap: 6px;
  }

  .sibling-row {
    grid-template-columns: 1fr 1fr 1fr 1fr 90px;
    gap: 8px;
  }

  .actions {
    justify-content: stretch;
  }

  .btn, .btn-primary {
    width: 100%;
  }

  .field {
    margin-bottom: 0 !important;
  }

  #schoolType {
    margin-bottom: 15px;
  }

  #schoolType .check {
    gap: 4px;
    height: 14px;
    min-height: auto;
  }
}

/* NEU: Small Mobile (<=480px) fix + scrollbar */
@media (max-width: 480px) {
  /* Fixe Breite: nicht verkleinerbar -> ggf. horizontales Scrollen */
  .page {
    width: 480px;
    min-width: 480px;

    height: 100dvh;
    overflow: auto; /* vertikal + horizontal scrollbar, falls nötig */
    -webkit-overflow-scrolling: touch;
    padding: 12px;
    box-sizing: border-box;
  }

  .card.lp {
    width: 100%;
    min-width: 100%;
    max-width: none;

    max-height: calc(100dvh - 24px);
    overflow: auto;
    -webkit-overflow-scrolling: touch;
    box-sizing: border-box;
  }

  .grid2 {
    grid-template-columns: 1fr 1fr !important;
    gap: 8px;
  }

  .grid3,
  .sibling-row {
    grid-template-columns: 1fr 1fr 1fr;
    margin-bottom: 16px;
  }

  .input {
    width: 100%;
  }

  .actions {
    justify-content: stretch;
  }

  .btn, .btn-primary {
    width: 100%;
    height: 44px;
  }

  .field {
    margin-bottom: 0 !important;
  }

  #schoolType {
    margin-bottom: 15px;
  }

  #schoolType .check {
    gap: 4px;
    height: 14px;
    min-height: auto;
  }
}
</style>
